import type { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { WebGlTextureWrapper, WebGlWrapper } from './webGlWrapper';
import { tryGetWebGlTextureFromVideoFrame } from './webGlTextureBuffer';
import { tryVideoFrameAsTexImageSource } from './texImageSource';
import type { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import type { Resolution } from '@mimir/common/transcoding/types';

export interface VideoFrameShaderReadAccess {
  /**
   *  Call using the linked program to bind the necessary textures.
   */
  bind(gl: WebGL2RenderingContext, program: WebGLProgram, textureSlotOffset: number): void;

  /*
   *  The main shader program must call a function with this name in order to
   *  read the video frame. The function signature is:
   *
   *     vec4 functionName(uv: vec2)
   */
  readonly readFunctionName: string;

  /**
   *  The source code for the uniform definitions and the shader read function.
   *  Must be included in the main shader program in order for this video frame
   *  shader access to work.
   */
  readonly readFunctionSource: string;

  /**
   *  Close returns the textures to the texture cache
   */
  close(): void;
}

export function getVideoFrameShaderAccess(
  webGlWrapper: WebGlWrapper,
  videoFrame: VideoFrameV2,
  bufferCache: BufferCache,
  forceSystemMemorySource: boolean,
): VideoFrameShaderReadAccess {
  if (!forceSystemMemorySource) {
    // Check first if this is a video frame backed by a texture
    const directTexture = tryGetWebGlTextureFromVideoFrame(videoFrame);
    if (directTexture !== null) {
      const videoBuffer = videoFrame.buffer.addReference();
      return new VideoFrameShaderReadAccessInternal(
        [{
          name: RGBA_TEXTURE_NAME,
          textureWrapper: directTexture,
          // Since the texture is owned by the video buffer we override the
          // resource release procedure for this uniform so that we instead
          // reduce the ref count of the video buffer.
          releaseResources: () => videoBuffer.close()
        }],
        RGBA_FUNCTION_NAME,
        RGBA_FUNCTION_SOURCE,
      );
    }

    // Then check if it is backed by a texImageSource
    const texImageSource = tryVideoFrameAsTexImageSource(videoFrame);
    if (texImageSource) {
      const gl = webGlWrapper.getGlContext();
      const { width, height } = videoFrame.resolution;
      const textureWrapper =
        webGlWrapper.getTexture(
          gl.RGBA,
          width,
          height,
          { sourceType: 'texImageSource', texImageSource }
        );

      return new VideoFrameShaderReadAccessInternal(
        [{ name: RGBA_TEXTURE_NAME, textureWrapper }],
        RGBA_FUNCTION_NAME,
        RGBA_FUNCTION_SOURCE,
      );
    }
  } else {
    console.warn('Forcing system memory video frame source');
  }

  // Finally we create a texture from the system memory buffer as final
  // fallback.
  let videoFrameTextures : VideoFrameShaderReadAccessInternal | null = null;
  bufferCache.allocateBuffer(
    videoFrame.buffer.size(),
    (buffer: Uint8Array) => {
      videoFrameTextures = fromSystemMemory(webGlWrapper, videoFrame, buffer);
    }
  ).close();

  if (videoFrameTextures === null) {
    throw new Error(`Unsupported video frame pixel format: ${videoFrame.pixelFormat}`);
  }

  return videoFrameTextures;
}

function fromSystemMemory(
  webGlWrapper: WebGlWrapper,
  videoFrame: VideoFrameV2,
  tmpBuffer: Uint8Array,
): VideoFrameShaderReadAccessInternal | null {
  const gl = webGlWrapper.getGlContext();
  const { width, height } = videoFrame.resolution;

  videoFrame.buffer.copyTo(tmpBuffer);
  switch (videoFrame.pixelFormat) {
    case 'bgr0':
    case 'rgba': {
      const textureWrapper =
        webGlWrapper.getTexture(
          gl.RGBA,
          width,
          height,
          {
            sourceType: 'buffer',
            format: gl.RGBA,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: tmpBuffer,
          }
        );

      switch (videoFrame.pixelFormat) {
        case 'rgba': return new VideoFrameShaderReadAccessInternal(
          [{ name: RGBA_TEXTURE_NAME, textureWrapper }],
          RGBA_FUNCTION_NAME,
          RGBA_FUNCTION_SOURCE,
        );
        case 'bgr0': return new VideoFrameShaderReadAccessInternal(
          [{ name: BGR0_TEXTURE_NAME, textureWrapper }],
          BGR0_FUNCTION_NAME,
          BGR0_FUNCTION_SOURCE,
        );
      }
    }
    case 'yuv420p': {
      const yuv420Data =
        calculateYUV420Data(videoFrame.resolution, tmpBuffer);

      const lumaTextureWrapper =
        webGlWrapper.getTexture(
          gl.LUMINANCE,
          yuv420Data.lumaWidth,
          yuv420Data.lumaHeight,
          {
            sourceType: 'buffer',
            format: gl.LUMINANCE,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: yuv420Data.lumaBytes,
          }
        );

      const cbTextureWrapper =
        webGlWrapper.getTexture(
          gl.LUMINANCE,
          yuv420Data.chromaWidth,
          yuv420Data.chromaHeight,
          {
            sourceType: 'buffer',
            format: gl.LUMINANCE,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: yuv420Data.cbBytes,
          }
        );

      const crTextureWrapper =
        webGlWrapper.getTexture(
          gl.LUMINANCE,
          yuv420Data.chromaWidth,
          yuv420Data.chromaHeight,
          {
            sourceType: 'buffer',
            format: gl.LUMINANCE,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: yuv420Data.cbBytes,
          }
        );

      return new VideoFrameShaderReadAccessInternal(
        [
          { name: YUV_TEXTURE_NAME_LUMA, textureWrapper: lumaTextureWrapper },
          { name: YUV_TEXTURE_NAME_CB, textureWrapper: crTextureWrapper },
          { name: YUV_TEXTURE_NAME_CR, textureWrapper: cbTextureWrapper },
        ],
        YUV_FUNCTION_NAME,
        YUV_FUNCTION_SOURCE,
      );
    }
    case 'nv12': {
      const nv12Data = calculateNV12Data(videoFrame.resolution, tmpBuffer);

      const lumaTextureWrapper =
        webGlWrapper.getTexture(
          gl.LUMINANCE,
          nv12Data.lumaWidth,
          nv12Data.lumaHeight,
          {
            sourceType: 'buffer',
            format: gl.LUMINANCE,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: nv12Data.lumaBytes,
          }
        );

      const cbCrTextureWrapper =
        webGlWrapper.getTexture(
          gl.RG8,
          nv12Data.chromaWidth,
          nv12Data.chromaHeight,
          {
            sourceType: 'buffer',
            format: gl.RG,
            type: gl.UNSIGNED_BYTE,
            arrayBuffer: nv12Data.cbcrBytes,
          }
        );

      return new VideoFrameShaderReadAccessInternal(
        [
          { name: NV12_TEXTURE_NAME_LUMA, textureWrapper: lumaTextureWrapper },
          { name: NV12_TEXTURE_NAME_CBCR, textureWrapper: cbCrTextureWrapper },
        ],
        NV12_FUNCTION_NAME,
        NV12_FUNCTION_SOURCE,
      );
    }
  }
  return null;
}

interface Uniform {
  name: string,
  textureWrapper: WebGlTextureWrapper,
  releaseResources?: () => void,
}

class VideoFrameShaderReadAccessInternal implements VideoFrameShaderReadAccess {
  constructor(
    private uniforms: Uniform[],
    readonly readFunctionName: string,
    readonly readFunctionSource: string,
  ) {

  }

  bind(gl: WebGL2RenderingContext, program: WebGLProgram, textureSlotOffset: number): void {
    for (const uniform of this.uniforms) {
      gl.activeTexture(gl.TEXTURE0 + textureSlotOffset);
      gl.bindTexture(gl.TEXTURE_2D, uniform.textureWrapper.texture);
      const location = gl.getUniformLocation(program, uniform.name);
      gl.uniform1i(location, textureSlotOffset);
      textureSlotOffset++;
    }
  }

  close(): void {
    for (const uniform of this.uniforms.splice(0)) {
      if (uniform.releaseResources) {
        uniform.releaseResources();
      } else {
        uniform.textureWrapper.close();
      }
    }
  }
}

const RGBA_TEXTURE_NAME : string = 'rgbaTexture';
const RGBA_FUNCTION_NAME : string = `readFromRgbaTexture`;
const RGBA_FUNCTION_SOURCE : string = `
  uniform sampler2D ${RGBA_TEXTURE_NAME};
  vec4 ${RGBA_FUNCTION_NAME}(vec2 uv) {
    return texture(${RGBA_TEXTURE_NAME}, uv);
  }
`;

const BGR0_TEXTURE_NAME : string = 'bgr0Texture';
const BGR0_FUNCTION_NAME : string = 'readFrameBgr0Texture';
const BGR0_FUNCTION_SOURCE : string = `
  uniform sampler2D ${BGR0_TEXTURE_NAME};
  vec4 ${BGR0_FUNCTION_NAME}(vec2 uv) {
    return vec4(texture(${BGR0_TEXTURE_NAME}, uv).bgr, 1.0);
  }
`;

const YUV_TEXTURE_NAME_LUMA : string = 'yuvTextureLuma';
const YUV_TEXTURE_NAME_CB : string = 'yuvTextureCb';
const YUV_TEXTURE_NAME_CR : string = 'yuvTextureCr';
const YUV_FUNCTION_NAME : string = 'readFromYuvTexture';
const YUV_FUNCTION_SOURCE : string = `
  uniform sampler2D ${YUV_TEXTURE_NAME_LUMA};
  uniform sampler2D ${YUV_TEXTURE_NAME_CB};
  uniform sampler2D ${YUV_TEXTURE_NAME_CR};

  vec4 ${YUV_FUNCTION_NAME}(vec2 uv) {\
    float y  = texture(${YUV_TEXTURE_NAME_LUMA}, uv).r;
    float cb = texture(${YUV_TEXTURE_NAME_CB}, uv).r - 0.5;
    float cr = texture(${YUV_TEXTURE_NAME_CR}, uv).r - 0.5;

    float r = y + 1.402 * cr;
    float g = y - 0.344136 * cb - 0.714136 * cr;
    float b = y + 1.772 * cb;

    return vec4(r, g, b, 1.0);
  }
`;

const NV12_TEXTURE_NAME_LUMA : string = 'yuvTextureLuma';
const NV12_TEXTURE_NAME_CBCR : string = 'yuvTextureCbCr';
const NV12_FUNCTION_NAME : string = 'readFromYuvTexture';
const NV12_FUNCTION_SOURCE : string = `
  uniform sampler2D ${NV12_TEXTURE_NAME_LUMA};
  uniform sampler2D ${NV12_TEXTURE_NAME_CBCR};

  vec4 ${NV12_FUNCTION_NAME}(vec2 uv) {\
    float y  = texture(${NV12_TEXTURE_NAME_LUMA}, uv).r;
    vec2 cbcr = texture(${NV12_TEXTURE_NAME_LUMA}, uv).rg - 0.5;

    float r = y + 1.402 * cbcr.g;
    float g = y - 0.344136 * cbcr.r - 0.714136 * cbcr.g;
    float b = y + 1.772 * cbcr.r;

    return vec4(r, g, b, 1.0);
  }
`;

interface YUV420Data {
  lumaWidth: number;
  lumaHeight: number;
  lumaBytes: Uint8Array,
  chromaWidth: number;
  chromaHeight: number;
  cbBytes: Uint8Array,
  crBytes: Uint8Array,
}

function calculateYUV420Data(resolution: Resolution, bytes: Uint8Array): YUV420Data {
  const lumaWidth = resolution.width;
  const lumaHeight = resolution.height;
  const lumaByteCount = lumaWidth * lumaHeight;

  const chromaWidth = lumaWidth/ 2;
  const chromaHeight = lumaHeight / 2;
  const chromaByteCount = chromaWidth * chromaHeight;

  let offset = 0;
  const lumaBytes = bytes.subarray(offset, offset + lumaByteCount);
  offset += lumaByteCount;
  const cbBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;
  const crBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;

  return {
    lumaWidth,
    lumaHeight,
    lumaBytes,
    chromaWidth,
    chromaHeight,
    crBytes,
    cbBytes,
  };
}

interface NV12Data {
  lumaWidth: number;
  lumaHeight: number;
  lumaBytes: Uint8Array,
  chromaWidth: number;
  chromaHeight: number;
  cbcrBytes: Uint8Array,
}

function calculateNV12Data(resolution: Resolution, bytes: Uint8Array): NV12Data {
  const lumaWidth = resolution.width;
  const lumaHeight = resolution.height;
  const lumaByteCount = lumaWidth * lumaHeight;

  const chromaWidth = lumaWidth/ 2;
  const chromaHeight = lumaHeight / 2;
  const chromaByteCount = chromaWidth * chromaHeight * 2;

  let offset = 0;
  const lumaBytes = bytes.subarray(offset, offset + lumaByteCount);
  offset += lumaByteCount;
  const cbcrBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;

  return {
    lumaWidth,
    lumaHeight,
    lumaBytes,
    chromaWidth,
    chromaHeight,
    cbcrBytes,
  };
}
